import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Phone,
  Globe,
  Clock,
  Mail,
  MessageSquare,
  Facebook,
  Instagram,
  Twitter,
  Lightbulb,
  Code2,
  GraduationCap,
  Monitor,
  Building2,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Users,
} from 'lucide-react';

import AnimatedCard from '../components/AnimatedCard';
import Seo from '../seo/Seo';
import { CONTACT_SEO } from '../seo/pageSeo';

const SOCIALS = [
  {
    Icon: MessageSquare,
    label: 'WhatsApp',
    url: 'https://wa.me/919306468015',
    color: '#25d366',
  },
  {
    Icon: Facebook,
    label: 'Facebook',
    url: 'https://www.facebook.com/profile.php?id=61580798271625',
    color: '#1877f2',
  },
  {
    Icon: Instagram,
    label: 'Instagram',
    url: 'https://www.instagram.com/_vtcodex/',
    color: '#e1306c',
  },
  {
    Icon: Twitter,
    label: 'Twitter',
    url: 'https://x.com/_VTCODEX',
    color: '#1da1f2',
  },
];

const CONTACT_ITEMS = [
  {
    Icon: Phone,
    label: 'Office',
    lines: ['+91 9499130923'],
    color: '#00f5ff',
  },
  {
    Icon: Users,
    label: 'Founder',
    lines: ['+91 9729013924'],
    color: '#8b5cf6',
  },
  {
    Icon: Users,
    label: 'Co-Founder',
    lines: ['+91 9306468015'],
    color: '#f472b6',
  },
  {
    Icon: Globe,
    label: 'Website',
    lines: ['www.vtcodex.com'],
    color: '#00f5ff',
  },
  {
    Icon: Clock,
    label: 'Working Hours',
    lines: ['Mon – Sat · 9AM – 8PM'],
    color: '#8b5cf6',
  },
  {
    Icon: Mail,
    label: 'Email',
    lines: [
      'contact@vtcodex.com',
      'vtcodex1128@gmail.com',
    ],
    color: '#f472b6',
  },
];

const TABS = [
  {
    id: 'project',
    label: 'Project Inquiry',
    Icon: Code2,
    color: '#00f5ff',
    headline: 'Let’s Build Something Amazing',
    sub: 'Tell us about your website, app or complete business solution.',
    formSrc:
      'https://docs.google.com/forms/d/e/1FAIpQLSdwhi4AG_dFg78XXfRkn4wAlkogo2UOZRtLJojDQh_xKQPLdw/viewform?embedded=true',
    pills: [
      'Website Development',
      'Mobile Apps',
      'SEO & Marketing',
      'Free Consultation',
    ],
  },
  {
    id: 'course',
    label: 'Course Demo',
    Icon: GraduationCap,
    color: '#8b5cf6',
    headline: 'Book Your Free Demo',
    sub: 'Join our online or offline demo classes before enrollment.',
    formSrc:
      'https://docs.google.com/forms/d/e/1FAIpQLSfYCUj3GYPbUxiIE8Ww4aIrU8hDOP_8cB6IvBfD-6EOaq22Wg/viewform?embedded=true',
    pills: [
      'Online Classes',
      'Offline Classes',
      'Free Demo',
      'Career Guidance',
    ],
  },
];

const DOTS = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 5 + 2,
  color: ['#00f5ff','#8b5cf6','#f472b6'][i % 3],
  dur: (Math.random() * 3 + 2).toFixed(1),
  delay: (Math.random() * 4).toFixed(1),
}));

export default function ContactPage() {

  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState('project');

  useEffect(() => {

    const tab = searchParams.get('tab');

    if (tab === 'course') {
      setActiveTab('course');
    } else {
      setActiveTab('project');
    }

  }, [searchParams]);

  const tab = TABS.find(t => t.id === activeTab);

  return (
    <>
      <Seo {...CONTACT_SEO} />
      {/* HERO */}
      <section
        className="relative min-h-[70vh] flex items-center overflow-hidden px-8 py-20"
      >

        {/* background */}
        <div className="absolute inset-0">

          <div className="absolute inset-0 bg-[#050810]" />

          <div
            className="absolute -left-52 top-1/3 w-[700px] h-[700px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(0,245,255,.13) 0%,transparent 70%)',
              filter:'blur(60px)'
            }}
          />

          <div
            className="absolute -right-52 top-1/2 w-[650px] h-[650px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(139,92,246,.15) 0%,transparent 70%)',
              filter:'blur(60px)'
            }}
          />

          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:'linear-gradient(rgba(0,245,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,255,.03) 1px,transparent 1px)',
              backgroundSize:'50px 50px'
            }}
          />

          {DOTS.map(dot => (
            <span
              key={dot.id}
              className="absolute rounded-full"
              style={{
                left:`${dot.left}%`,
                top:`${dot.top}%`,
                width:`${dot.size}px`,
                height:`${dot.size}px`,
                background:dot.color,
                boxShadow:`0 0 ${dot.size * 4}px ${dot.color}`,
                animation:`dotPulse ${dot.dur}s ease-in-out ${dot.delay}s infinite alternate`,
                opacity:.6
              }}
            />
          ))}

        </div>

        {/* content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center">

          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs tracking-widest mb-8"
          >
            <Sparkles size={14} />
            GET IN TOUCH WITH VTCODEX
          </div>

          <h1
            className="text-white font-bold leading-tight mb-6"
            style={{
              fontSize:'clamp(2.8rem,7vw,5.5rem)'
            }}
          >
            Let’s Create <br />
            <span className="grad-text">
              Something Powerful
            </span>
          </h1>

          <p className="text-slate-400 max-w-3xl mx-auto text-lg leading-relaxed mb-12">
            Whether you need a premium website, mobile app or want
            to start your learning journey — we’re here to help.
          </p>

          {/* tabs */}
          <div className="flex justify-center gap-4 flex-wrap">

            {TABS.map((t, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(t.id)}
                className="flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300"
                style={{
                  background:
                    activeTab === t.id
                      ? `linear-gradient(135deg,${t.color},#8b5cf6)`
                      : 'rgba(255,255,255,.03)',

                  color:
                    activeTab === t.id
                      ? '#fff'
                      : '#94a3b8',

                  border:
                    activeTab === t.id
                      ? 'none'
                      : `1px solid ${t.color}25`,

                  transform:
                    activeTab === t.id
                      ? 'translateY(-4px)'
                      : 'none',

                  boxShadow:
                    activeTab === t.id
                      ? `0 0 25px ${t.color}40`
                      : 'none',
                }}
              >
                <t.Icon size={18} />
                {t.label}
              </button>
            ))}

          </div>

        </div>

      </section>

      {/* MAIN */}
      <section className="py-24 px-8 bg-[#0a0f1e]">

        <div className="max-w-7xl mx-auto">

          {/* top strip */}
          <div
            className="rounded-[2rem] border p-10 mb-10 text-center"
            style={{
              background:`linear-gradient(135deg,${tab.color}08,rgba(139,92,246,.05))`,
              border:`1px solid ${tab.color}20`
            }}
          >

            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{
                background:`${tab.color}15`,
                border:`1px solid ${tab.color}30`
              }}
            >
              <tab.Icon size={30} color={tab.color} />
            </div>

            <h2 className="text-white text-4xl font-bold mb-4">
              {tab.headline}
            </h2>

            <p className="text-slate-400 max-w-2xl mx-auto mb-8">
              {tab.sub}
            </p>

            <div className="flex flex-wrap justify-center gap-3">

              {tab.pills.map((pill, i) => (
                <span
                  key={i}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{
                    background:`${tab.color}10`,
                    border:`1px solid ${tab.color}25`,
                    color:tab.color
                  }}
                >
                  {pill}
                </span>
              ))}

            </div>

          </div>

          {/* grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.7fr] gap-8">

            {/* left */}
            <div className="flex flex-col gap-5">

              {CONTACT_ITEMS.map((item, i) => (
                <AnimatedCard
                  key={i}
                  delay={i * 0.08}
                  className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0d1428] p-5 transition-all duration-500 hover:-translate-y-1"
                >

                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background:`${item.color}15`,
                      border:`1px solid ${item.color}30`
                    }}
                  >
                    <item.Icon size={24} color={item.color} />
                  </div>

                  <div>

                    <p className="text-slate-500 text-xs tracking-widest uppercase mb-1">
                      {item.label}
                    </p>

                    {item.lines.map((line, idx) => (
                      <p
                        key={idx}
                        className="text-slate-200 text-sm"
                      >
                        {line}
                      </p>
                    ))}

                  </div>

                </AnimatedCard>
              ))}

              {/* socials */}
              <AnimatedCard
                delay={0.4}
                className="rounded-2xl border border-white/10 bg-[#0d1428] p-6"
              >

                <p className="text-slate-500 text-xs tracking-widest uppercase mb-5">
                  Follow Us
                </p>

                <div className="flex gap-3 flex-wrap">

                  {SOCIALS.map((s, i) => (
                    <a
                      key={i}
                      href={s.url}
                      target="#"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 hover:-translate-y-2"
                      style={{
                        background:`${s.color}10`,
                        border:`1px solid ${s.color}25`,
                        color:s.color,
                        boxShadow:`0 0 0px ${s.color}`
                      }}
                    >
                      <s.Icon size={22} />
                    </a>
                  ))}

                </div>

              </AnimatedCard>

              {/* quick connect */}
              <AnimatedCard
                delay={0.5}
                className="rounded-[2rem] border border-cyan-400/10 bg-cyan-400/[0.04] p-7"
              >

                <div className="flex items-center gap-2 mb-4">

                  <Lightbulb
                    size={18}
                    color="#00f5ff"
                  />

                  <p className="text-cyan-400 font-semibold">
                    Quick Response
                  </p>

                </div>

                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  Chat directly with our team on WhatsApp and
                  get quick guidance for your project or course.
                </p>

                <a
                  href="https://wa.me/919306468015"
                  target="#"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white no-underline transition-all duration-300 hover:-translate-y-1"
                  style={{
                    background:'linear-gradient(135deg,#25d366,#128c7e)'
                  }}
                >
                  <MessageSquare size={18} />
                  Chat Now
                  <ArrowRight size={16} />
                </a>

              </AnimatedCard>

            </div>

            {/* right */}
            <AnimatedCard
              delay={0.1}
              className="rounded-[2rem] border border-white/10 bg-[#0d1428] p-6"
            >

              <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/5">

                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{
                    background:`${tab.color}15`,
                    border:`1px solid ${tab.color}30`
                  }}
                >
                  <tab.Icon size={22} color={tab.color} />
                </div>

                <div>

                  <p className="text-white font-semibold">
                    {tab.label}
                  </p>

                  <p className="text-slate-500 text-xs tracking-widest uppercase">
                    Fill The Form Below
                  </p>

                </div>

              </div>

              {/* form */}
              <div className="rounded-2xl overflow-hidden border border-white/5">

                <iframe
                  src={tab.formSrc}
                  title={tab.label}
                  className="w-full border-0 block"
                  style={{
                    minHeight:'650px',
                    filter:'invert(1) hue-rotate(180deg) brightness(.88) saturate(.75)'
                  }}
                  loading="lazy"
                />

              </div>

              <p className="text-slate-500 text-xs text-center mt-5 flex items-center justify-center gap-2">
                <Clock size={12} />
                We usually reply within
                <span className="text-cyan-400">
                  2 Hours
                </span>
              </p>

            </AnimatedCard>

          </div>

        </div>

      </section>

      <style>{`

        @keyframes dotPulse {

          from {
            opacity:.25;
            transform:scale(1);
          }

          to {
            opacity:.8;
            transform:scale(1.8);
          }

        }

      `}</style>
    </>
  );
}